import { Form, Formik, FormikHelpers } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { jwtDecode } from 'jwt-decode';
import { ChangeEvent, useMemo, useState } from 'react';
import { FiLogIn } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { USERNAME_EMAIL_REGEX } from '../../constants/settings';
import { useAuthContext } from '../../contexts/AuthProvider';
import useAuthActions from '../../hooks/APIHooks/users/useAuthActions';
import { logInFormSchema } from '../../schemas';
import { DecodedToken } from '../../types/common';
import { setToken } from '../../utils/cookieHandler';
import { fetchErrorMessage } from '../../utils/fetchErrorMessage';
import InputFieldBadge from '../CommonComps/Badges/InputFieldBadge';
import AcademiaButton from '../CommonComps/Buttons/AcademiaButton';
import AcademiaInputField from '../CommonComps/Inputs/AcademiaInputField';
import BadgeLoading from '../CommonComps/Loadings/BadgeLoading';
import ForgotPasswordModal from '../CommonComps/Modals/ForgotPasswordModal';
import SocialLogins from './SocialLogins';

const LoginForm = () => {
  // integration of react hooks here
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isLoadingUsername, setIsLoadingUsername] = useState<boolean>(false);
  const [usernameOrEmail, setUsernameOrEmail] = useState<string>('');
  const [userFullName, setUserFullName] = useState<string>('');

  // integration of react-router-dom hooks here
  const navigate = useNavigate();

  // integration of context hooks here
  const { setCurrentUserId, setCurrentUserType, setIsFirstLogin } =
    useAuthContext();

  // integration of API hooks here
  const { userLoginHandler, getUserNameByEmailHandler } = useAuthActions();

  // loading user's full name based on valid username or email here
  useMemo(async () => {
    if (USERNAME_EMAIL_REGEX.test(usernameOrEmail)) {
      try {
        setIsLoadingUsername(true);

        await getUserNameByEmailHandler
          .mutateAsync({ body: { username_or_email: usernameOrEmail } })
          .then((res) => {
            if (res.is_success) {
              setUserFullName(res.data);
            } else {
              setUserFullName('');
            }
          });
      } catch (error) {
        setUserFullName('');
      } finally {
        setIsLoadingUsername(false);
      }
    } else {
      setUserFullName('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usernameOrEmail]);

  // handler function to handle log in form submission declared here
  const loginFormSubmissionHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      setCurrentUserId(0);
      setCurrentUserType('');
      setIsFirstLogin(false);

      actions.setSubmitting(true);

      console.log(values);

      await userLoginHandler
        .mutateAsync({
          username_or_email: values.username_or_email,
          password: values.password,
        })
        .then((value) => {
          console.log('LOGIN RES: ', value.data);
          if (value?.is_success && value.data) {
            setToken(value.data.refresh_token, value.data.access_token);

            const decodedToken = jwtDecode<DecodedToken>(
              value.data.access_token
            );

            console.log('TOKEN VALUE: ', value.data, decodedToken);

            setCurrentUserId(decodedToken.user_id);
            setCurrentUserType(decodedToken.user_type);

            localStorage.clear();

            toast.success('Login Successful!!');

            if (
              decodedToken.user_type === 'user' &&
              !decodedToken.is_approved
            ) {
              navigate('/application/update');
              return;
            }

            setIsFirstLogin(decodedToken.is_first_login);
            navigate('/dashboard');
          } else {
            console.log('ERROR', userLoginHandler);
            toast.error(value.message || 'Login Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering login form component here
  return (
    <motion.div
      initial={{ x: 500, opacity: 0 }}
      animate={{
        x: 0,
        opacity: 1,
        transition: {
          ease: 'easeInOut',
          duration: 0.4,
        },
      }}
      className='w-[95%] md:w-2/3 2xl:w-3/5 mx-auto my-auto p-5 rounded-xl shadow-academia-sm'
    >
      <div className='space-y-1 mb-5'>
        <h1 className='text-3xl 2xl:text-4xl italic text-legal-dark-blue font-bold'>
          Log In
        </h1>
        <p className='text-slate-500'>
          Please, login to your account by completing the following form.
        </p>
      </div>

      <Formik
        initialValues={{ username_or_email: '', password: '' }}
        validationSchema={logInFormSchema}
        onSubmit={loginFormSubmissionHandler}
      >
        {(props) => (
          <Form className='space-y-4'>
            <AcademiaInputField
              type='text'
              name='username_or_email'
              label='Username Or Email'
              placeholder='username_123 || username@example.com'
              value={usernameOrEmail}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                props.setFieldValue('username_or_email', e.target.value);
                setUsernameOrEmail(e.target.value);
              }}
              isRequired
              badge={
                isLoadingUsername ? (
                  <BadgeLoading />
                ) : userFullName ? (
                  <InputFieldBadge
                    badgeText=''
                    badgeValue={userFullName}
                    isValid
                  />
                ) : null
              }
            />

            <AcademiaInputField
              type='password'
              name='password'
              label='Password'
              placeholder='pas5W0rd'
              isRequired
            />

            <div className='flex items-center justify-end'>
              <button
                type='button'
                onClick={() => setShowModal(true)}
                className='text-base-light-blue italic text-sm underline hover:opacity-60 active:scale-95 duration-300'
              >
                Forgot Password?
              </button>
            </div>

            <div className='!mt-5'>
              <AcademiaButton
                type='submit'
                icon={<FiLogIn />}
                isLoading={props.isSubmitting}
                variant='premium'
                fullWidth
              >
                Log In
              </AcademiaButton>

              <p className='text-sm text-legal-dark-blue text-center mt-2'>
                New Here?{' '}
                <Link
                  to={'/signup'}
                  className='text-base-light-blue italic underline hover:opacity-60 active:scale-95 duration-300'
                >
                  Create New Account
                </Link>
              </p>
            </div>
          </Form>
        )}
      </Formik>

      <SocialLogins />

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showModal && (
          <ForgotPasswordModal handleModalClose={() => setShowModal(false)} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default LoginForm;
